import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InputGroup } from '@/components/ui/input-group';
import { Checkbox as CheckboxInput } from '@/components/ui/checkbox';
import styles from '../UnifyOnboarding.module.scss';

export const PlugAndPlayConnection = ({
	provider,
	formData,
	formErrors,
	sessionId,
	isLoading,
	isDisabled,
	isPolling,
	uploadKey,
	hasExistingCredentials,
	customPosName,
	onFormChange,
	onConnect
}) => {
	if (!provider || provider.connection !== 'plug-play') {
		return null;
	}

	const renderField = (field, fieldIndex) => {
		if (field.type === 'ftp-details') {
			const variant = field.variant || 'ftp';
			let title, description;

			switch (variant) {
				case 'email':
					title = 'Email address';
					description = 'Use this unique email address for your location.';
					break;
				case 'uploadKey':
					title = 'Upload Key';
					description = 'Use this unique upload key for your location.';
					break;
				default:
					title = 'FTP Password';
					description = 'Use host name ftp.inventory-connect.com and username apikey to upload your stock files.';
					break;
			}

			return (
				<div key={fieldIndex} className={styles.ftpDetails}>
					<p className={styles.helperText}>{description}</p>
					{variant === 'ftp' && (
						<>
							<InputGroup label="FTP host name">
								<Input value="ftp.inventory-connect.com" readOnly />
							</InputGroup>
							<InputGroup label="Username">
								<Input value="apikey" readOnly />
							</InputGroup>
						</>
					)}
					{variant === 'email' ? (
						<div>
							<label className={styles.fieldLabel}>{title}</label>
							<div className={styles.emailDisplay}>{uploadKey ? `${uploadKey}@inventory-connect.com` : 'Loading...'}</div>
						</div>
					) : (
						<InputGroup label={title}>
							<Input value={uploadKey || 'Loading...'} readOnly />
						</InputGroup>
					)}
				</div>
			);
		}

		if (field.type === 'checkbox') {
			return (
				<div key={fieldIndex} className={styles.checkboxField}>
					<CheckboxInput
						label={field.label}
						checked={formData[field.saveAs] === 'true'}
						onChange={(e) => onFormChange(field.saveAs, e.target.checked ? 'true' : '')}
					/>
					{field.helpText && <p className={styles.helperText}>{field.helpText}</p>}
				</div>
			);
		}

		const inputType = field.type || 'text';
		const hasError = field.saveAs && formErrors[field.saveAs];
		return (
			<InputGroup key={fieldIndex} label={field.label}>
				<Input
					type={inputType}
					value={formData[field.saveAs] || ''}
					onChange={(e) => onFormChange(field.saveAs, e.target.value)}
					className={hasError ? styles.inputError : ''}
				/>
				{hasError ? (
					<p className={styles.errorText}>{formErrors[field.saveAs]}</p>
				) : (
					field.helpText && <p className={styles.helperText}>{field.helpText}</p>
				)}
			</InputGroup>
		);
	};

	// For plug-and-play connectors:
	// - Always show "Finish integration" button once provider is selected
	// - Session will be created on-demand when clicking if it doesn't exist yet
	const isSpinnerType = provider.connect?.submitType === 'spinner';

	return (
		<div className={styles.plugPlayForm}>
			{/* Custom POS Integration Message - Use provider.value not provider.id */}
			{provider.value === 'email' && customPosName && (
				<div className={styles.customPosMessage}>
					<p>
						<strong>We don&apos;t currently have a direct integration with {customPosName}.</strong>
					</p>
					<p className={styles.helperText}>
						We suggest using the report export functionality in {customPosName} to the specifications below, for an email
						upload.
					</p>
					<p className={styles.helperText}>We will investigate a direct integration with {customPosName} in the meantime.</p>
				</div>
			)}

			{hasExistingCredentials && (
				<div className={styles.productDataEnabled}>
					<p>
						<strong>✓ Connection credentials configured</strong>
					</p>
					<p className={styles.helperText}>Your credentials are already set up. You can update them below if needed.</p>
				</div>
			)}

			{provider.connect?.steps &&
				provider.connect.steps.map((step, stepIndex) => (
					<div key={stepIndex} className={styles.formStep}>
						<h4>{step.label}</h4>
						{step.description && (
							<div
								className={styles.stepDescription}
								dangerouslySetInnerHTML={{
									__html: Array.isArray(step.description) ? step.description.join('<br/>') : step.description
								}}
							/>
						)}
						{step.fields && step.fields.map((field, fieldIndex) => renderField(field, fieldIndex))}
						{step.buttons && step.buttons.length > 0 && (
							<div className={styles.buttonGroup}>
								{step.buttons.map((button, buttonIndex) => (
									<Button
										key={buttonIndex}
										secondary={button.variant === 'secondary'}
										href={button.href}
										target="_blank"
										rel="noreferrer"
									>
										{button.label}
									</Button>
								))}
							</div>
						)}
					</div>
				))}

			{isSpinnerType && isPolling && sessionId ? (
				<div className={styles.pollingMessage}>
					<p>Waiting for inventory upload...</p>
					<p className={styles.helperText}>
						Please ensure your POS system is configured to send inventory files. This may take a few minutes.
					</p>
				</div>
			) : (
				<Button
					onClick={onConnect}
					disabled={isLoading || isDisabled || !sessionId || Object.keys(formErrors).length > 0}
					className={styles.submitButton}
				>
					{isLoading ? 'Saving...' : !sessionId ? 'Preparing connection...' : 'Finish integration'}
				</Button>
			)}
		</div>
	);
};
